import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import type { ExperienceMode } from '@/modules/account-context/core/accountContext';
export {
  EXPERIENCE_MODE_DESCRIPTIONS,
  EXPERIENCE_MODE_LABELS,
} from '@/modules/account-context/core/accountContext';
export type { ExperienceMode } from '@/modules/account-context/core/accountContext';

interface ExperienceModeAccess {
  isSuperAdmin: boolean;
  isBusinessAdmin: boolean;
  isStaff: boolean;
}

const STORAGE_KEY = 'experience-mode.current';
const subscribers = new Set<() => void>();
let cachedStoredMode: ExperienceMode | null | undefined;

const notifySubscribers = () => {
  subscribers.forEach((listener) => listener());
};

const readStorageValue = (key: string) => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorageValue = (key: string, value: string | null) => {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
      return;
    }

    window.localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures in unsupported environments.
  }
};

const getStoredModeSnapshot = (): ExperienceMode | null => {
  if (cachedStoredMode === undefined) {
    cachedStoredMode = (readStorageValue(STORAGE_KEY) as ExperienceMode | null) ?? null;
  }

  return cachedStoredMode;
};

const writeStoredModeSnapshot = (mode: ExperienceMode | null) => {
  cachedStoredMode = mode;
  writeStorageValue(STORAGE_KEY, mode);
  notifySubscribers();
};

const handleWindowStorage = (event: StorageEvent) => {
  if (event.key !== STORAGE_KEY) return;
  cachedStoredMode = (event.newValue as ExperienceMode | null) ?? null;
  notifySubscribers();
};

const subscribeToStoredMode = (listener: () => void) => {
  subscribers.add(listener);

  if (typeof window !== 'undefined' && subscribers.size === 1) {
    window.addEventListener('storage', handleWindowStorage);
  }

  return () => {
    subscribers.delete(listener);
    if (typeof window !== 'undefined' && subscribers.size === 0) {
      window.removeEventListener('storage', handleWindowStorage);
    }
  };
};

const resolveAvailableModes = ({ isSuperAdmin, isBusinessAdmin, isStaff }: ExperienceModeAccess): ExperienceMode[] => {
  const modes: ExperienceMode[] = [];

  if (isSuperAdmin) {
    modes.push('super_admin');
  }

  modes.push('player');

  if (isSuperAdmin || isBusinessAdmin) {
    modes.push('business_admin');
  }

  if (isSuperAdmin || isStaff) {
    modes.push('staff');
  }

  return modes;
};

export const useExperienceMode = (access: ExperienceModeAccess) => {
  const { isSuperAdmin, isBusinessAdmin, isStaff } = access;
  const availableModes = useMemo(
    () => resolveAvailableModes({ isSuperAdmin, isBusinessAdmin, isStaff }),
    [isSuperAdmin, isBusinessAdmin, isStaff],
  );
  const storedMode = useSyncExternalStore(subscribeToStoredMode, getStoredModeSnapshot, () => null);

  const resolveDefaultMode = useCallback(() => {
    if (availableModes.includes('super_admin')) {
      return 'super_admin';
    }

    if (availableModes.includes('business_admin')) {
      return 'business_admin';
    }

    if (availableModes.includes('staff')) {
      return 'staff';
    }

    return 'player';
  }, [availableModes]);

  const selectedMode =
    storedMode && availableModes.includes(storedMode) ? storedMode : resolveDefaultMode();

  useEffect(() => {
    if (storedMode !== selectedMode) {
      writeStoredModeSnapshot(selectedMode);
    }
  }, [selectedMode, storedMode]);

  const setSelectedMode = useCallback(
    (mode: ExperienceMode) => {
      if (!availableModes.includes(mode)) return;
      writeStoredModeSnapshot(mode);
    },
    [availableModes],
  );

  return {
    availableModes,
    selectedMode,
    setSelectedMode,
    hasMultipleModes: availableModes.length > 1,
  };
};

